<template>
  <div class="main-container">
    <SidebarNav @update:sidebarNavCollapsed="sidebarNavCollapsed = $event" />
    <SearchBar :sidebar-nav-collapsed="sidebarNavCollapsed" />
    <div class="reserve-balance-container" :class="{['reserve-balance-container-sidebar-collapse']: sidebarNavCollapsed}">
      <div class="reserve-balance-sub-container">
        <div class="header">
          <div class="table-header">
            {{ $t('balanceReserve.title') }}
          </div>
          <div class="table-header-description">
            {{ $t('balanceReserve.description') }}
            <span class="add-funds-link" @click="navigateToAddFunds">
              {{ $t('balanceReserve.addFunds') }} <LinkIcon class="link-icon" />
            </span>
          </div>
        </div>
        
        <!-- Balance Cards Section -->
        <div class="balance-cards-wrapper">
          <div class="balance-cards-meta">
            <span v-if="updatedAt" class="balance-cards-updated-at">
              {{ $t('balanceReserve.updatedAt') }} {{ formattedUpdatedAt }}
            </span>
            <ToolTip
              placement="top"
              :arrow="true"
              :content="$t('balanceReserve.syncBalancesTooltip')"
            >
              <img src="../app/assets/images/info.svg" alt="info" class="sync-info-icon" />
            </ToolTip>
            <button
              class="sync-button"
              :disabled="syncing"
              @click="syncBalanceAccounts"
            >
              <svg
                class="sync-icon"
                :class="{ 'sync-icon-spinning': syncing }"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              {{ $t('balanceReserve.syncBalances') }}
            </button>
          </div>
          <div class="balance-cards-container">
            <div v-if="loading" class="loading-container">
              <div class="loading-shimmer"></div>
            </div>
            <template v-else>
              <BalanceCard
                v-for="account in filteredBalanceAccounts"
                :key="account.id"
                :balance-account="account"
                :show-info-icon="true"
              />
            </template>
          </div>
        </div>

        <div class="nav">
          <div
            class="nav-item"
            v-for="tab in tabs"
            :key="tab.name"
            :class="{'nav-item-active': tab.name === currentTab, [tab.name]: true}"
            @click="() => switchTab(tab.name)"
          >
            <span>{{ $t('balanceReserve.tabs.' + tab.name) }}</span>
          </div>
        </div>
        <!-- Tab Content Section -->
        <div class="tab-content-container">
          <ReserveTransactionsTable v-if="currentTab === 'ACTIVITY'" />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import SidebarNav from '@/components/SidebarNav.vue';
import SearchBar from '@/components/SearchBar.vue';
import LinkIcon from '@/components/icons/LinkIcon.vue';
import BalanceCard from '@/components/reserve-balance/BalanceCard.vue';
import ReserveTransactionsTable from '@/components/reserve-balance/ReserveTransactionsTable.vue';
import ToolTip from '@/components/common/ToolTip.vue';
import { navigateTo } from '@/router';
import { RouteConstants } from '@/router/routeConstants';
import { mapGetters } from 'vuex';
import merchantBalanceAccount from '@/api/merchantBalanceAccount';
import Formatter from '@/app/utils/common/functions/formatter';

export default {
  name: 'BalanceReserveView',
  components: { 
    SearchBar, 
    SidebarNav, 
    LinkIcon, 
    BalanceCard, 
    ReserveTransactionsTable,
    ToolTip
  },
  data() {
    return {
      sidebarNavCollapsed: false,
      windowWidth: window.innerWidth,
      balanceAccounts: [], // Array of MerchantBalanceAccountResponseDto
      loading: false,
      syncing: false,
      updatedAt: null,
      currentTab: "ACTIVITY",
      tabs: [
        {
          name: "ACTIVITY",
        },
      ],
    }
  },
  computed: {
    ...mapGetters('app', ['getMerchantId', 'getRollingReserveConfig', 'getUpfrontReserveConfig']),
    filteredBalanceAccounts() {
      return this.balanceAccounts.filter(account => {
        // Hide ROLLING_RESERVE if getRollingReserveConfig is null
        if (account.type === 'ROLLING_RESERVE' && !this.getRollingReserveConfig) {
          return false;
        }
        // Hide UPFRONT_RESERVE if getUpfrontReserveConfig is null
        if (account.type === 'UPFRONT_RESERVE' && !this.getUpfrontReserveConfig) {
          return false;
        }
        return true;
      });
    },
    formattedUpdatedAt() {
      if (!this.updatedAt) return '';
      return Formatter.format({ type: 'zonedDateWithTime', data: this.updatedAt, format: 'dd MMM yyyy, HH:mm' });
    },
  },
  mounted() {
    this.fetchBalanceAccounts();
  },
  methods: {
    navigateToAddFunds() {
      navigateTo(RouteConstants.ADD_FUNDS, true, this.$route.query);
    },
    switchTab(tabName) {
      this.currentTab = tabName;
    },
    async fetchBalanceAccounts() {
      try {
        this.loading = true;
        const response = await merchantBalanceAccount.fetchMerchantBalanceAccount(this.getMerchantId);
        this.applyBalanceAccountResponse(response);
      } catch (error) {
        this.balanceAccounts = [];
        this.$danger(this.$t('transactions.error.message', { error: error?.source?.detail || error?.message }), { dismissable: true, timeout: 10000 });
      } finally {
        this.loading = false;
      }
    },
    async syncBalanceAccounts() {
      try {
        this.syncing = true;
        const response = await merchantBalanceAccount.syncMerchantBalanceAccount(this.getMerchantId);
        this.applyBalanceAccountResponse(response);
      } catch (error) {
        this.$danger(this.$t('transactions.error.message', { error: error?.source?.detail || error?.message }), { dismissable: true, timeout: 10000 });
      } finally {
        this.syncing = false;
      }
    },
    applyBalanceAccountResponse(response) {
      const order = ['CURRENT', 'REFUND_RESERVE', 'CHARGEBACK_RESERVE', 'ROLLING_RESERVE', 'UPFRONT_RESERVE'];
      this.balanceAccounts = (response.merchantBalanceAccounts || []).sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type));
      const rawUpdatedAt = response.updated_at;
      this.updatedAt = rawUpdatedAt
        ? (rawUpdatedAt instanceof Date ? rawUpdatedAt.toISOString() : String(rawUpdatedAt))
        : new Date().toISOString();
    },
  }
}
</script>
<style scoped lang="scss">
.main-container {
  background: $neutral_50;
  height: 100%;
  overflow: scroll;
}

.reserve-balance-container {
  left: 256px;
  top: 0;
  position: relative;
  width: calc(100% - 256px);
  padding: 20px 30px 30px 30px;
  transition: all 0.2s ease-in;
  justify-content: center;
  display: flex;

  & .reserve-balance-sub-container {
    width: 100%;
    max-width: 1360px;

    & .title-description {
      text-align: left;
      margin-top: 20px;
      font-size: 14px;
      font-weight: $weight_normal;
    }
  }

  &.reserve-balance-container-sidebar-collapse {
    left: 86px;
    width: calc(100% - 86px);
  }

  & .empty-placeholder-icon {
    display: flex;
    width: 40px;
    height: 40px;
  }

  & .table-header {
    font-style: normal;
    font-weight: $fw_bold;
    font-size: $fs_h4;
    line-height: $fs_h2;
    color: $neutral_1000;
    text-align: left;
    padding: $fs_h4 0 0 0;
  }

  & .table-header-description {
    font-style: normal;
    font-weight: $fw_normal;
    font-size: 12px;
    line-height: 18px;
    color: $neutral_600;
    text-align: left;
    padding: 8px 0 24px 0;

    & .add-funds-link {
      color: $primary_600;
      font-weight: $fw_bold;
      cursor: pointer;
      text-decoration: underline;
      display: inline-flex;
      align-items: center;
      gap: 4px;

      &:hover {
        color: $primary_800;
      }

      & .link-icon {
        flex-shrink: 0;
        margin-left: 2px;
        width: 12px;
        height: 12px;
      }
    }
  }

  .balance-cards-wrapper {
    display: flex;
    flex-direction: column;
    gap: $space_ti;
  }

  .balance-cards-meta {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: $space_sm;
  }

  .balance-cards-updated-at {
    font-size: $fs_caption;
    color: $neutral_600;
    font-weight: $fw_normal;
  }

  .balance-cards-meta ::v-deep .c-tooltip {
    display: inline-flex;
    align-items: center;
  }

  .sync-info-icon {
    width: 15px;
    height: 15px;
    cursor: pointer;
    opacity: 0.6;
    flex-shrink: 0;
    display: block;

    &:hover {
      opacity: 1;
    }
  }

  .sync-button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: $base_white;
    border: 1.5px solid $primary_600;
    border-radius: 6px;
    cursor: pointer;
    font-size: $fs_body;
    font-weight: $fw_medium;
    color: $primary_600;
    padding: 8px 16px;
    line-height: 1;
    transition: background 0.15s, border-color 0.15s, color 0.15s;

    &:hover:not(:disabled) {
      background: $primary_50;
      border-color: $primary_700;
      color: $primary_700;
    }

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }

  .sync-icon {
    flex-shrink: 0;

    &.sync-icon-spinning {
      animation: spin 0.8s linear infinite;
    }
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .balance-cards-container {
    display: flex;
    flex-direction: row;
    gap: $space_xs;
    flex-wrap: nowrap;
    overflow-x: auto;
    
    @media (max-width: 768px) {
      flex-direction: column;
      gap: $space_xs;
      overflow-x: visible;
    }
    
    .loading-container {
      display: flex;
      width: 100%;
      justify-content: center;
      padding: $space_md;
    }
    
    .loading-shimmer {
      width: 200px;
      height: 80px;
      background: linear-gradient(90deg, $neutral_100 25%, $neutral_200 50%, $neutral_100 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
      border-radius: $radius_lg;
    }
    
    @keyframes shimmer {
      0% {
        background-position: -200% 0;
      }
      100% {
        background-position: 200% 0;
      }
    }
  }

  & .nav {
    display: flex;
    flex-direction: row;
    font-size: 14px;
    font-weight: $weight_bold;
    background-color: transparent;
    padding-left: 0;
    border-bottom: 1px solid $neutral_200;
    margin-bottom: 16px;

    &:after {
      content: '';
      height: 2px;
      background-color: $color_white;
    }

    & .nav-item {
      color: $neutral_600;
      display: flex;
      cursor: pointer;
      align-items: start;
      margin-right: 16px;

      @media screen and (min-width: 801px) {
        padding: 12px 16px;
      }

      @media screen and (max-width: 800px) {
        padding: 10px 16px;
      }

      &:hover {
        background-color: $primary_fill_light;
      }

      &.disabled:hover {
        background-color: $color-white;
        cursor: not-allowed;
      }
    }

    & .nav-item-active {
      color: $primary_600;
      border-bottom: 2px solid $primary_400;

      & svg {
        margin-right: 10px;
        color: $primary_text;
      }
    }
  }

  .tabs-container {
    margin-top: $space_lg;
    background-color: $color_white;
    border-radius: 8px;
    border: 1px solid $neutral_100;
    overflow: hidden;
  }

  .tab-content-container {
    margin-top: $space_md;
  }

  .summary-container {
    background-color: $color_white;
    width: 100%;
    height: fit-content;
    border: 1px solid $neutral_100;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    .milestones {
      width: 100%;
      height: 56px;
      background: #EAECEE;
      border-radius: 8px 8px 0 0;
      padding: 16px;

      & .milestones-header {
        font-style: normal;
        font-weight: $weight_bold;
        font-size: 16px;
        line-height: 30px;
        color: $neutral_1000;
        display: flex;
        justify-content: space-between;
      }
    }

    .s-top-0 {
      top: unset !important;
    }

    .s-absolute {
      position: unset !important;
    }

    .id {
      font-weight: $weight_normal;
      color: $neutral_1000;
      width: 45px
    }


    .steps-title {
      font-style: normal;
      font-weight: $weight_medium;
      font-size: 16px;
      line-height: 24px;
      color: $neutral_1000;
      text-align: left;
    }


  }
}
</style>